import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface BubblesEffectProps {
  intensity?: number;      // 0-1
  particleCount?: number;
  areaSize?: [number, number]; // width, depth
  color?: string;
  maxHeight?: number;
}

export function BubblesEffect({
  intensity = 0.5,
  particleCount = 100,
  areaSize = [20, 20],
  color = '#a0e0ff',
  maxHeight = 10,
}: BubblesEffectProps) {
  const groupRef = useRef<THREE.Group>(null);

  const actualCount = Math.floor(particleCount * intensity);

  // Initialize bubbles
  const bubbles = useMemo(() => {
    const result = [];
    for (let i = 0; i < actualCount; i++) {
      result.push({
        position: new THREE.Vector3(
          (Math.random() - 0.5) * areaSize[0],
          Math.random() * maxHeight * 0.3, // Start near bottom
          (Math.random() - 0.5) * areaSize[1]
        ),
        speed: 0.5 + Math.random() * 1,
        wobblePhase: Math.random() * Math.PI * 2,
        wobbleSpeed: 1 + Math.random() * 2,
        wobbleAmount: 0.1 + Math.random() * 0.2,
        scale: 0.05 + Math.random() * 0.1,
        opacity: 0.3 + Math.random() * 0.4,
      });
    }
    return result;
  }, [actualCount, areaSize[0], areaSize[1], maxHeight]);

  useFrame((state, delta) => {
    if (!groupRef.current) return;

    const time = state.clock.elapsedTime;

    groupRef.current.children.forEach((child, i) => {
      const bubble = bubbles[i];
      if (!bubble) return;

      // Rise upward
      bubble.position.y += bubble.speed * delta;

      // Wobble side to side
      const wobble = Math.sin(time * bubble.wobbleSpeed + bubble.wobblePhase) * bubble.wobbleAmount;
      child.position.x = bubble.position.x + wobble;
      child.position.y = bubble.position.y;
      child.position.z = bubble.position.z + Math.cos(time * bubble.wobbleSpeed * 0.7 + bubble.wobblePhase) * bubble.wobbleAmount * 0.5;

      // Scale pulsing
      const scalePulse = 1 + Math.sin(time * 3 + bubble.wobblePhase) * 0.1;
      child.scale.setScalar(bubble.scale * scalePulse);

      // Reset when reaching top
      if (bubble.position.y > maxHeight) {
        bubble.position.y = -0.5;
        bubble.position.x = (Math.random() - 0.5) * areaSize[0];
        bubble.position.z = (Math.random() - 0.5) * areaSize[1];
      }
    });
  });

  if (actualCount === 0) return null;

  return (
    <group ref={groupRef}>
      {bubbles.map((bubble, i) => (
        <group key={i} position={bubble.position}>
          {/* Outer glow */}
          <mesh>
            <sphereGeometry args={[bubble.scale * 1.5, 12, 12]} />
            <meshBasicMaterial
              color={color}
              transparent
              opacity={bubble.opacity * 0.3 * intensity}
            />
          </mesh>
          {/* Inner bubble */}
          <mesh>
            <sphereGeometry args={[bubble.scale, 12, 12]} />
            <meshStandardMaterial
              color="#ffffff"
              emissive={color}
              emissiveIntensity={0.3}
              transparent
              opacity={bubble.opacity * intensity}
              roughness={0}
              metalness={0.1}
            />
          </mesh>
        </group>
      ))}
    </group>
  );
}
